import React, {useContext, useMemo} from 'react';
import {IncompleteDischargeCRF} from '@/api/dto/dto';
import SortableTable, {SORT_ORDER, TableColumn, TableData} from '@common/SortableTable';
import {SortOrderType} from '@clients/ClientsPage';
import DisplayHelpers from '@/helpers/display_helpers';
import {SearchParam} from "@/helpers/search_params";
import {ColoredTableLink} from "@common/TableLink.styles";
import AssignedToClient from "@clients/AssignedToClient";
import styled from '@emotion/styled';
import {FormCodeBinding} from "@/api/dto/forms";
import {useQuery} from "@tanstack/react-query";
import {CarecoApiContext} from "@/app-context/careco-api-context";

const TableWrapper = styled.div`
    margin-top: 2rem;
`;

interface Props {
  clients: IncompleteDischargeCRF[];
  loading: boolean;
  fetching: boolean;
  loadError: Error | null;
  searchTerm: string;
  searchFunction: (searchTerm: string) => void;
  sortFunction: (sortKey: TableColumn, sortOrder: SortOrderType) => void;
  onNextPage: () => void;
}

const tableHeaders = [
  'Client name',
  'Client Id',
  'Group',
  'Client discharge date',
  'No of Days since discharge',
  'Assigned to',
  'Incomplete form',
];

const AdmissionDischargeCRFTable = ({
  clients,
  loading,
  fetching,
  loadError,
  searchTerm,
  searchFunction,
  sortFunction,
  onNextPage,
}: Props) => {
  const {carecoApi} = useContext(CarecoApiContext);
  const { data: rosters, isLoading } = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi
  });
  const tableData: TableData[] = useMemo(() => {
    return clients.map((client): TableData => {
      const clientRoster = rosters?.data.data.find(roster => roster.id === client.rosterId);
      const [firstName, middleName, lastName] = client.clientName.split(' ');
      const clientName = DisplayHelpers.formatName(firstName, middleName, lastName);
      const daysSinceDischarge = DisplayHelpers.daysSince(client.clientDischargeDate);
      return {
        clientName: {
          tableHeader: 'Client name',
          key: 'clientName',
          type: 'string',
          value: clientName,
          displayValue: (
            <ColoredTableLink
              to={`/clients/profile?${SearchParam.PATIENT_ID}=${client.patientId}&${SearchParam.ROSTER_ID}=${client.rosterId}`}>
              {clientName}
            </ColoredTableLink>
          ),
          columnWidth: '20%',
        },
        clientId: {
          tableHeader: 'Client Id',
          key: 'clientId',
          type: 'string',
          value: client.clientId,
          displayValue: client.clientId,
          columnWidth: '10%',
        },
        groupAdmissionDischarge: {
          tableHeader: 'Group',
          key: 'groupAdmissionDischarge',
          type: 'string',
          value: client.clientGroup,
          displayValue: client.clientGroup,
          columnWidth: '15%',
        },
        dischargeDate: {
          tableHeader: 'Client discharge date',
          key: 'clientDischargeDate',
          type: 'date',
          value: client.clientDischargeDate,
          displayValue: DisplayHelpers.formatDateFromDateOnlyString(client.clientDischargeDate),
          columnWidth: '15%',
        },
        daysSinceDischarge: {
          tableHeader: 'No of Days since discharge',
          key: 'clientDischargeDate',
          type: 'date',
          value: client.clientDischargeDate,
          displayValue: daysSinceDischarge.toString(),
          columnWidth: '15%',
        },
        assignedTo: {
          tableHeader: 'Assigned to',
          key: 'assignedUser',
          type: 'string',
          value: '',
          displayValue: <AssignedToClient client={client.client} roster={clientRoster} />,
          columnWidth: '10%',
        },
        incompleteForm: {
          tableHeader: 'Incomplete form',
          key: 'incompleteForm',
          type: 'string',
          value: '',
          displayValue: (
            <ColoredTableLink
              to = {`/clients/profile/forms`+
                          `?${SearchParam.PATIENT_ID}=${client.patientId}` +
                          `&${SearchParam.ROSTER_ID}=${client.rosterId}` +
                          `&${SearchParam.FORM_TYPE}=${client.clientAdmissionForm
                            ? FormCodeBinding.DSAMH_CRF_ADMISSION_FORM
                            : FormCodeBinding.DSAMH_CRF_DISCHARGE_FORM }` +
                          `&${SearchParam.FORM_ENCOUNTER}=${client.encounterId}` +
                          `&${SearchParam.FORM_EVENT_TYPE}=${rosters?.data.data.find(roster =>
                            roster.id === client.rosterId)?.typeDisplayName ?? ''}` +
                          `&${SearchParam.FORM_EVENT_DATE}=${client.clientDischargeDate}`}>

              {client.clientAdmissionForm && <div>Admission CRF</div>}
              {client.clientDischargeForm && <div>Discharge CRF</div>}
            </ColoredTableLink>

          ),
          columnWidth: '15%',
        },
      };
    });
  }, [clients, rosters]);

  return (
    <TableWrapper>
      <SortableTable
        id="admission-discharge-crfs-table"
        data={tableData}
        loading={loading || isLoading}
        fetching={fetching}
        loadError={loadError}
        header={tableHeaders}
        searchColumns={[]}
        searchPlaceholder=""
        excludeFilterAndSearch={true}
        sortableColumns={tableData.length > 0 ? [
          tableData[0]?.dischargeDate,
          tableData[0]?.daysSinceDischarge,
        ] : []}
        tableOverrides={{
          searchFunction,
          searchTerm,
          sortFunction,
          initialSort: {
            tableColumn: {
              tableHeader: 'Client discharge date',
              key: 'clientDischargeDate',
              type: 'date',
              value: '',
              displayValue: '',
            },
            sortOrder: SORT_ORDER.ASC,
          },
        }}
        onNextPage={onNextPage}
      />
    </TableWrapper>
  );
};

export default AdmissionDischargeCRFTable;
